import { StyledModal } from '~/components/dialogs/dialog.style';
import { Link, LinkProps } from '~/components/link';
import { ScrollablePdf } from '~/components/terms/scrollable-terms/scrollable-pdf.component';
import { useToggle } from '~/hooks';
import { SignupContractVersion } from '~/services/fetch-personalized-signup-contract-set-from-template';
import { IS_PERCH_ENERGY } from '~/utils/constants';
import {
  hasPerchPersonalizationFailure,
  hasAnyPerchPersonalizedPdf,
} from '~/utils/contract-personalization';
import { ContractLinkScreen, Logger } from '~/utils/logger';
import { MultiContractScrollView } from './multi-contract-scroll-view.component';

function normalizePdfDataUrl(base64: string): string {
  return base64.startsWith('data:') ? base64 : `data:application/pdf;base64,${base64}`;
}

export type PersonalizedPreviewItem = { key: string; pdfUrl: string };

/** PDF previews for the modal (usage_request batch, legacy single field, or static file URL). */
export function getPersonalizedPreviewItems(
  contract: SignupContractVersion,
): PersonalizedPreviewItem[] {
  const { id, base64PerchPersonalizedFiles, base64PerchPersonalizedFile, fileUrl, presignedUrl } =
    contract;
  if (base64PerchPersonalizedFiles?.length) {
    return base64PerchPersonalizedFiles
      .filter(e => e.base64PerchPersonalizedFile)
      .map(e => ({
        key: `${id}-ua-${e.utilityAccountId ?? 'na'}`,
        pdfUrl: normalizePdfDataUrl(e.base64PerchPersonalizedFile as string),
      }));
  }
  if (presignedUrl) {
    return [
      { key: new URL(contract.presignedUrl!).pathname.split('/').pop()!, pdfUrl: presignedUrl },
    ];
  }
  if (base64PerchPersonalizedFile) {
    return [
      { key: `${id}-perch-single`, pdfUrl: normalizePdfDataUrl(base64PerchPersonalizedFile) },
    ];
  }
  if (fileUrl) {
    return [{ key: `${id}-file`, pdfUrl: fileUrl }];
  }
  return [];
}

export function getPersonalizedPreviewPdfUrls(contract: SignupContractVersion): string[] {
  return getPersonalizedPreviewItems(contract).map(i => i.pdfUrl);
}

export const ModalContractLink = ({
  contracts,
  linkProps,
  screen,
}: {
  contracts: SignupContractVersion[];
  linkProps?: LinkProps;
  screen: ContractLinkScreen;
}) => {
  const [isOpen, toggle] = useToggle(false);

  const primaryContract = contracts[0];
  const { base64PerchPersonalizedFile } = primaryContract;

  const hasPresignedUrl = contracts.some(c => !!c.presignedUrl);
  const previewItems = getPersonalizedPreviewItems(contracts[0]);

  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    toggle(true);
    if (IS_PERCH_ENERGY) {
      // Status-less appends: opening a preview is an attribute of the screen, not a
      // lifecycle transition, so it must never re-render a completed screen.
      // (SUBEXP-3193)
      if (hasAnyPerchPersonalizedPdf(contracts[0])) {
        Logger.logEvent(
          {
            contractPreviewOpened: true,
            personalizedContractReceived: true,
          },
          screen,
        );
      } else if (hasPerchPersonalizationFailure(contracts[0])) {
        Logger.logEvent(
          {
            contractPreviewOpened: true,
            personalizedContractReceived: false,
          },
          screen,
        );
      }
    }
    event.preventDefault();
  };

  return (
    <>
      {!!isOpen && (
        <StyledModal
          isOpen={isOpen}
          onDismiss={() => {
            toggle(false);
          }}
          size="large"
          aria-label="personalized-contract-preview"
          data-testid="personalized-contract-preview"
        >
          <StyledModal.HeaderCentered
            title={primaryContract.displayName}
            onClose={() => {
              toggle(false);
            }}
          />
          {hasPresignedUrl || previewItems.length > 0 ? (
            <MultiContractScrollView contracts={contracts} />
          ) : (
            <ScrollablePdf pdfUrl={base64PerchPersonalizedFile || primaryContract.fileUrl || ''} />
          )}
        </StyledModal>
      )}
      <Link
        color="primary"
        id={`ContractVersion-${primaryContract.id}`}
        onClick={handleOnClick}
        {...linkProps}
        openInNewTab={false}
      >
        {primaryContract.displayName}
      </Link>
    </>
  );
};
